import { useState } from "react";
import { Link } from "react-router-dom";

const quickLinks = [
  { label: "Shop All Products", href: "https://ironhavensupply.com", external: true },
  { label: "Authorized Brands", href: "/authorized-brands", external: false },
  { label: "EcoFlow — Coming Soon", href: "/ecoflow", external: false, badge: "New" },
  { label: "About Us", href: "/about", external: false },
  { label: "Contact", href: "/contact", external: false },
];

const shopLinks = [
  { label: "Home Backup Power", href: "https://ironhavensupply.com/collections/home-backup-power", external: true },
  { label: "Off-Grid Systems", href: "https://ironhavensupply.com/collections/off-grid-power-systems", external: true },
  { label: "Preparedness Systems", href: "https://ironhavensupply.com/collections/preparedness-systems", external: true },
  { label: "Storage Sheds", href: "https://ironhavensupply.com/collections/storage-sheds", external: true },
];

const supportLinks = [
  { label: "Order Tracking", href: "/order-tracking", external: false },
  { label: "FAQ", href: "/faq", external: false },
  { label: "Solar vs Gas vs Whole Home", href: "/compare-backup-power-systems", external: false },
  { label: "Installation Guides", href: "/installation-guides", external: false },
  { label: "Warranty Info", href: "/warranty", external: false },
];

const trustItems = [
  { icon: "ri-shield-star-line", label: "Authorized U.S. Dealer" },
  { icon: "ri-customer-service-2-line", label: "Real Customer Support" },
  { icon: "ri-lock-2-line", label: "Secure Checkout" },
  { icon: "ri-award-line", label: "Manufacturer Warranties" },
];

const socialLinks = [
  { icon: "ri-facebook-fill", href: "https://www.facebook.com/ironhavensupply", label: "Facebook" },
  { icon: "ri-instagram-line", href: "https://www.instagram.com/ironhavensupply", label: "Instagram" },
  { icon: "ri-youtube-line", href: "https://www.youtube.com/@ironhavensupply", label: "YouTube" },
  { icon: "ri-tiktok-line", href: "https://www.tiktok.com/@ironhavensupply", label: "TikTok" },
  { icon: "ri-twitter-x-line", href: "https://x.com/ironhavensupply", label: "X (Twitter)" },
];

function FooterLink({ href, label, external, badge }: { href: string; label: string; external: boolean; badge?: string }) {
  const content = (
    <>
      <span>{label}</span>
      {badge && (
        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
          {badge}
        </span>
      )}
    </>
  );
  
  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-400 text-sm hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
      >
        {content}
      </a>
    );
  }
  return (
    <Link to={href} className="text-gray-400 text-sm hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center">
      {content}
    </Link>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch("https://readdy.ai/api/form/d72h1ccjuufb4jdbl460", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Trust bar */}
      <div className="border-b border-gray-800 bg-gray-950 py-4">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {trustItems.map((t) => (
            <div key={t.label} className="flex items-center gap-2 whitespace-nowrap">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${t.icon} text-amber-400 text-base`}></i>
              </div>
              <span className="text-gray-300 text-xs font-medium">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter strip */}
      <div className="border-b border-gray-800 py-10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-lg">Stay Prepared. Stay Informed.</p>
            <p className="text-gray-400 text-sm mt-1">Get outage preparedness tips and exclusive deals from Iron Haven.</p>
          </div>
          {submitted ? (
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-base"></i>
              </div>
              You&apos;re subscribed — stay ready!
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 w-full md:w-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 md:w-64 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-amber-500"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {submitting ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Main footer grid */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand / Contact Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-flashlight-fill text-xl text-amber-500"></i>
            </div>
            <span className="font-extrabold text-white text-base tracking-tight">Iron Haven Supply</span>
          </div>
          <p className="text-gray-500 text-xs mb-4">Furman Loomis LLC</p>
          <p className="text-gray-400 text-sm leading-relaxed mb-5">
            A U.S.-based authorized dealer of home backup power systems, solar generators, off-grid energy solutions, and preparedness equipment.
          </p>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Address</p>
              <p className="text-gray-400 text-sm">2645 Broad St<br />Bethel Park, PA 15102</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Phone</p>
              <a href="tel:8666054975" className="text-amber-400 text-sm hover:underline cursor-pointer">(866) 605-4975</a>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Email</p>
              <a href="mailto:support@ironhavensupply.com" className="text-amber-400 text-sm hover:underline cursor-pointer">support@ironhavensupply.com</a>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Support Hours</p>
              <p className="text-gray-400 text-sm">Mon–Fri, 9am–5pm EST</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">EIN</p>
              <p className="text-gray-400 text-sm">EIN: 41-3937806 (Furman Loomis LLC)</p>
            </div>
          </div>

          {/* Social media links */}
          <div className="mt-5">
            <p className="text-xs text-gray-500 mb-2.5">Follow Us</p>
            <div className="flex items-center gap-3 flex-wrap">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-amber-500 text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Links — all point to Shopify */}
        <div>
          <h4 className="font-bold text-white text-sm mb-4 uppercase tracking-wider" id="quick-links">
            Quick Links
          </h4>
          <ul className="space-y-2.5">
            {quickLinks.map((l) => (
              <li key={l.label}>
                <FooterLink href={l.href} label={l.label} external={l.external} badge={l.badge} />
              </li>
            ))}
          </ul>
        </div>

        {/* Shop by Category */}
        <div>
          <h4 className="font-bold text-white text-sm mb-4 uppercase tracking-wider" id="shop-categories">
            Shop by Category
          </h4>
          <ul className="space-y-2.5">
            {shopLinks.map((l) => (
              <li key={l.label}>
                <FooterLink href={l.href} label={l.label} external={l.external} />
              </li>
            ))}
          </ul>

          <h4 className="font-bold text-white text-sm mt-7 mb-4 uppercase tracking-wider" id="resources">
            Resources
          </h4>
          <ul className="space-y-2.5">
            {supportLinks.map((l) => (
              <li key={l.label}>
                <FooterLink href={l.href} label={l.label} external={l.external} />
              </li>
            ))}
          </ul>
        </div>

        {/* Policies — most link to Shopify canonical source, MAP is internal */}
        <div>
          <h4 className="font-bold text-white text-sm mb-4 uppercase tracking-wider" id="policies">
            Policies
          </h4>
          <ul className="space-y-2.5">
            {[
              { label: "Privacy Policy", href: "https://ironhavensupply.com/policies/privacy-policy", external: true },
              { label: "Terms of Service", href: "https://ironhavensupply.com/policies/terms-of-service", external: true },
              { label: "Shipping Policy", href: "https://ironhavensupply.com/pages/shipping-policy", external: true },
              { label: "Return Policy", href: "https://ironhavensupply.com/pages/return-refund-policy", external: true },
              { label: "Cookie Policy", href: "https://ironhavensupply.com/policies/privacy-policy", external: true },
              { label: "MAP Policy", href: "/map-policy", external: false },
            ].map((l) => (
              <li key={l.label}>
                <FooterLink href={l.href} label={l.label} external={l.external} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800 py-5">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} Iron Haven Supply — Furman Loomis LLC · EIN: 41-3937806 · All rights reserved.
          </p>
          <p className="text-gray-600 text-xs">
            2645 Broad St, Bethel Park, PA 15102 · <a href="tel:8666054975" className="hover:text-amber-400 transition-colors cursor-pointer">(866) 605-4975</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
